import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { fmtCurrency, fmtCompact, fmtDate } from "@/lib/format";
import { accountTypeMeta } from "@/lib/investmentTypes";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    ChevronLeft, PlusCircle, TrendingUp, TrendingDown, History, Pencil, Trash2, Layers, CalendarClock,
} from "lucide-react";
import {
    AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

function tickDate(v) {
    const [y, m, d] = v.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function InvestmentDetail() {
    const { aid } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const cur = user?.currency || "USD";
    const { isMember, memberName } = useHousehold();

    const [account, setAccount] = useState(null);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [logging, setLogging] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: a }, { data: e }] = await Promise.all([
                api.get(`/investments/${aid}`),
                api.get(`/investments/${aid}/entries`),
            ]);
            setAccount(a);
            setEntries(e);
        } catch (e) {
            // 402 = Free plan (Investments is Pro+) — /investments itself shows the upsell card.
            toast.error(e?.response?.status === 402 ? "Investments is a Pro feature" : "Account not found");
            navigate("/investments");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [aid]);

    const chartPoints = useMemo(() => {
        return [...entries].reverse().map(e => ({ date: e.occurred_at, value: parseFloat(e.amount) }));
    }, [entries]);

    const onDeleteEntry = async () => {
        if (!deleteId) return;
        try {
            await api.delete(`/investments/${aid}/entries/${deleteId}`);
            toast.success("Entry removed");
            load();
        } catch { toast.error("Failed to delete"); }
        finally { setDeleteId(null); }
    };

    if (loading || !account) {
        return <div className="card-elev rounded-2xl p-10 text-center text-slate-400 text-sm">Loading…</div>;
    }

    const meta = accountTypeMeta(account.account_type);
    const Icon = meta.icon;
    const color = account.color || meta.color;
    const value = parseFloat(account.current_value);
    const change = parseFloat(account.change_amount);
    const up = change >= 0;
    const first = entries.length ? entries[entries.length - 1] : null;
    const isOwner = account.user_id === user.id;

    return (
        <div className="space-y-4" data-testid="investment-detail-page">
            <div className="flex items-center gap-3">
                <Link to="/investments" data-testid="back-to-investments" className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                    <ChevronLeft className="w-5 h-5" />
                </Link>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 mb-0.5">{meta.label}</p>
                    <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 truncate flex items-center gap-2" data-testid="account-name">
                        {account.name}
                        {isMember && !isOwner ? (
                            <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(account.user_id)}'s</span>
                        ) : null}
                    </h1>
                </div>
                {isOwner ? (
                    <Button onClick={() => setLogging(true)} data-testid="dt-log-balance" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl gap-1.5 h-11 px-5 flex-shrink-0">
                        <PlusCircle className="w-4 h-4" /> Log balance
                    </Button>
                ) : null}
            </div>

            {/* Summary + chart */}
            <div className="card-elev rounded-2xl p-6 sm:p-7">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
                    <div className="flex items-center gap-4">
                        <span className="w-14 h-14 rounded-2xl grid place-items-center flex-shrink-0" style={{ backgroundColor: `${color}18`, color }}>
                            <Icon className="w-7 h-7" />
                        </span>
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 mb-0.5">Current value</p>
                            <div className="font-display text-4xl font-extrabold text-slate-900 tabular-nums" data-testid="account-value">{fmtCurrency(value, cur)}</div>
                            {entries.length > 0 ? (
                                <div className={`inline-flex items-center gap-1 mt-1 text-sm font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>
                                    {up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                                    {up ? "+" : ""}{fmtCurrency(change, cur)} ({up ? "+" : ""}{account.change_percent.toFixed(1)}%) all time
                                </div>
                            ) : (
                                <p className="text-xs text-slate-400 mt-1">No balance logged yet</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="h-64">
                    {chartPoints.length < 2 ? (
                        <div className="h-full grid place-items-center text-sm text-slate-400 text-center px-6">
                            {chartPoints.length === 0 ? "Log a balance to start this account's chart." : "Log one more balance to see a trend line."}
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartPoints} margin={{ left: -12, right: 8, top: 8, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="g-account" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={color} stopOpacity={0.32} />
                                        <stop offset="100%" stopColor={color} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#F1F5F9" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="date" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={tickDate} minTickGap={40} />
                                <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => fmtCompact(v, cur)} />
                                <Tooltip
                                    labelFormatter={(v) => fmtDate(v)}
                                    formatter={(v) => [fmtCurrency(v, cur), account.name]}
                                    contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 8px 24px rgba(15,23,42,0.06)" }}
                                />
                                <Area type="monotone" dataKey="value" stroke={color} fill="url(#g-account)" strokeWidth={2.5} />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* Stat row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <MiniStat icon={TrendingUp} accent={up ? "emerald" : "rose"} label="All-time change" value={`${up ? "+" : ""}${fmtCurrency(change, cur)}`} sub={`${up ? "+" : ""}${entries.length ? account.change_percent.toFixed(1) : "0.0"}%`} />
                <MiniStat icon={CalendarClock} accent="indigo" label="First logged" value={first ? fmtCurrency(first.amount, cur) : "—"} sub={first ? fmtDate(first.occurred_at) : "No entries yet"} />
                <MiniStat icon={History} accent="amber" label="Entries" value={String(entries.length)} sub={account.last_updated ? `Updated ${fmtDate(account.last_updated)}` : "—"} />
                <MiniStat icon={Layers} accent="indigo" label="Account type" value={meta.label} sub={account.notes ? account.notes : "No notes"} />
            </div>

            {/* History */}
            <div className="card-elev rounded-2xl p-6">
                <div className="flex items-center gap-2.5 mb-4">
                    <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 grid place-items-center"><History className="w-4 h-4" /></span>
                    <div>
                        <h3 className="font-display text-lg font-bold text-slate-900">Balance history</h3>
                        <p className="text-sm text-slate-500">{entries.length} logged entries</p>
                    </div>
                </div>

                {entries.length === 0 ? (
                    <EmptyState
                        icon={History}
                        title="No entries yet"
                        description="Log your first balance to start tracking this account."
                        action={isOwner ? <Button onClick={() => setLogging(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"><PlusCircle className="w-4 h-4 mr-1.5" /> Log balance</Button> : null}
                    />
                ) : (
                    <div className="divide-y divide-slate-100" data-testid="entry-history-list">
                        {entries.map(e => (
                            <div key={e.id} data-testid={`entry-${e.id}`} className="flex items-center gap-3 py-3.5 group">
                                <span className="w-10 h-10 rounded-xl grid place-items-center flex-shrink-0" style={{ backgroundColor: `${color}18`, color }}>
                                    <TrendingUp className="w-4 h-4" />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-slate-900 truncate">{e.notes || "Balance update"}</div>
                                    <div className="text-xs text-slate-500">{fmtDate(e.occurred_at, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</div>
                                </div>
                                <div className="font-display font-bold tabular-nums text-slate-900">{fmtCurrency(e.amount, cur)}</div>
                                {isOwner ? (
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => setEditingEntry(e)} data-testid={`entry-edit-${e.id}`} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>
                                        <button onClick={() => setDeleteId(e.id)} data-testid={`entry-delete-${e.id}`} className="p-1.5 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <EntryDialog
                open={logging || !!editingEntry}
                accountId={aid}
                accountName={account.name}
                entry={editingEntry}
                onClose={() => { setLogging(false); setEditingEntry(null); }}
                onSaved={load}
            />

            <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                        <AlertDialogDescription>This removes one logged balance point from the account's history.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={onDeleteEntry} className="bg-rose-600 hover:bg-rose-700 text-white">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function MiniStat({ icon: Icon, accent, label, value, sub }) {
    const map = {
        emerald: "bg-emerald-50 text-emerald-600",
        rose: "bg-rose-50 text-rose-600",
        indigo: "bg-indigo-50 text-indigo-600",
        amber: "bg-amber-50 text-amber-600",
    };
    return (
        <div className="card-elev rounded-2xl p-5">
            <div className="flex items-start justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{label}</span>
                <span className={`w-8 h-8 rounded-lg grid place-items-center ${map[accent]}`}><Icon className="w-4 h-4" /></span>
            </div>
            <div className="font-display text-xl font-extrabold text-slate-900 tabular-nums truncate">{value}</div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">{sub}</div>
        </div>
    );
}

function EntryDialog({ open, accountId, accountName, entry, onClose, onSaved }) {
    const { user } = useAuth();
    const cur = user?.currency || "USD";
    const [amount, setAmount] = useState("");
    const [occurredAt, setOccurredAt] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const isEdit = !!entry;

    useEffect(() => {
        if (open) {
            setAmount(entry ? String(entry.amount) : "");
            setOccurredAt(entry ? entry.occurred_at : new Date().toISOString().slice(0, 10));
            setNotes(entry?.notes || "");
        }
    }, [open, entry]);

    if (!open) return null;

    const submit = async () => {
        const amt = parseFloat(amount);
        if (Number.isNaN(amt) || amt < 0) { toast.error("Enter a valid balance"); return; }
        try {
            setSaving(true);
            if (isEdit) {
                await api.patch(`/investments/${accountId}/entries/${entry.id}`, { amount: amt, occurred_at: occurredAt, notes: notes || null });
                toast.success("Entry updated");
            } else {
                await api.post(`/investments/${accountId}/entries`, { amount: amt, occurred_at: occurredAt, notes: notes || null });
                toast.success("Balance logged");
            }
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-md rounded-2xl" data-testid="entry-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">{isEdit ? "Edit entry" : "Log balance"} — {accountName}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Balance</Label>
                            <Input data-testid="ed-amount" type="number" step="0.01" min="0" autoFocus value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">As of date</Label>
                            <Input data-testid="ed-date" type="date" value={occurredAt} onChange={e => setOccurredAt(e.target.value)} />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-slate-700">Note (optional)</Label>
                        <Input data-testid="ed-note" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Market update, contribution, etc." />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button data-testid="ed-save" disabled={saving} onClick={submit} className="bg-indigo-600 hover:bg-indigo-700 text-white">{isEdit ? "Save changes" : "Save balance"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
